import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Chart, ChartConfiguration, registerables, ChartType } from 'chart.js';
import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs';

Chart.register(...registerables);

interface GraphSettings {
  type: 'pie' | 'bar' | 'line';
  xAxis?: string;
  yAxis?: string;
  valueField?: string;
  labelField?: string;
}

@Component({
  selector: 'app-graph',
  standalone: true,
  imports: [],
  template: `
    <div class="graph">
      <canvas #chartCanvas></canvas>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .graph {
        width: 100%;
        height: 100%;
        padding: 10px;
        box-sizing: border-box;
      }
    `,
  ],
})
export class GraphComponent implements OnInit, OnDestroy, AfterViewInit {
  private chart: Chart | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimeout: any;
  private dataSubject = new BehaviorSubject<any>(null);
  private settingsSubject = new BehaviorSubject<GraphSettings>({ type: 'bar' });

  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;

  @Input() set dataSource(value: any) {
    this.dataSubject.next(value);
  }

  @Input() set settings(value: GraphSettings) {
    this.settingsSubject.next(value);
  }

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    // Combine data and settings updates with debouncing
    this.dataSubject
      .pipe(
        debounceTime(250),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
      )
      .subscribe((data) => {
        if (data) {
          this.updateChart();
        }
      });

    this.settingsSubject
      .pipe(
        debounceTime(250),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
      )
      .subscribe((settings) => {
        if (settings) {
          this.updateChart();
        }
      });
  }

  ngAfterViewInit() {
    this.setupResizeObserver();
    // Initial chart creation if we have data
    if (this.dataSubject.getValue()) {
      this.updateChart();
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.chart) {
      this.chart.destroy();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.dataSubject.complete();
    this.settingsSubject.complete();
  }

  private setupResizeObserver() {
    if (!this.chartCanvas?.nativeElement) return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        if (this.chart) {
          this.chart.resize();
        }
      }, 250);
    });

    this.resizeObserver.observe(this.chartCanvas.nativeElement);
  }

  private updateChart() {
    if (!this.chartCanvas) {
      console.warn('Chart canvas not available');
      return;
    }

    const data = this.dataSubject.getValue();
    if (!data || !Array.isArray(data)) {
      console.warn('Invalid data source:', data);
      return;
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      console.warn('Could not get canvas context');
      return;
    }

    if (this.chart) {
      this.chart.destroy();
    }

    try {
      this.chart = new Chart(ctx, this.getChartConfig());
    } catch (error) {
      console.error('Error creating chart:', error);
    }
  }

  private getChartConfig(): ChartConfiguration {
    const data = this.dataSubject.getValue();
    const settings = this.settingsSubject.getValue();

    if (!data || !Array.isArray(data) || data.length === 0) {
      console.warn('Invalid or empty data source');
      return {
        type: settings.type,
        data: {
          labels: [],
          datasets: [
            {
              data: [],
              backgroundColor: [],
              borderColor: [],
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 250, // Fast animations for smooth updates
          },
        },
      };
    }

    const labels = data.map((item) => {
      if (!settings.labelField && !item.label) {
        console.warn(
          'No label field specified and item has no label property:',
          item
        );
        return 'No Label';
      }
      return settings.labelField
        ? item[settings.labelField]
        : item.label || 'No Label';
    });

    const values = data.map((item) => {
      if (!settings.valueField && !item.value) {
        console.warn(
          'No value field specified and item has no value property:',
          item
        );
        return 0;
      }
      const value = settings.valueField
        ? item[settings.valueField]
        : item.value;
      return typeof value === 'number' ? value : 0;
    });

    const colors = this.generateColors(values.length);

    const config: ChartConfiguration = {
      type: settings.type,
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderColor: colors.map((color) => color.replace('0.5', '1')),
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 250, // Fast animations for smooth updates
        },
        plugins: {
          legend: {
            display: settings.type === 'pie',
            position: 'top',
          },
        },
        scales:
          settings.type !== 'pie'
            ? {
                y: {
                  beginAtZero: true,
                  title: {
                    display: !!settings.yAxis,
                    text: settings.yAxis,
                  },
                },
                x: {
                  title: {
                    display: !!settings.xAxis,
                    text: settings.xAxis,
                  },
                },
              }
            : undefined,
      },
    };

    return config;
  }

  private generateColors(count: number): string[] {
    const baseColors = [
      'rgba(75, 192, 192, 0.5)',
      'rgba(54, 162, 235, 0.5)',
      'rgba(153, 102, 255, 0.5)',
      'rgba(255, 99, 132, 0.5)',
      'rgba(255, 206, 86, 0.5)',
      'rgba(255, 159, 64, 0.5)',
      'rgba(231, 233, 237, 0.5)',
    ];

    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(baseColors[i % baseColors.length]);
    }
    return result;
  }
}
